/**
 * Threshold Editor Component
 *
 * Editor for Attack Feasibility Thresholds (score ranges)
 */

import React, { useState } from 'react';
import { useConfiguration } from '../../../contexts/ConfigurationContext';
import InstructionPanel from '../../../components/ui/InstructionPanel/InstructionPanel';
import { Input } from '../../../components/ui/Input';
import { Slider } from '../../../components/ui/Slider';
import { getFeasibilityColor } from '../../../utils/severityStyle';
import styles from './ThresholdEditor.module.css';

const THRESHOLD_LEVELS = ['Very Low', 'Low', 'Medium', 'High'];
const GLOBAL_MIN = 0;
const GLOBAL_MAX = 59;

export default function ThresholdEditor() {
  const { config, updateConfiguration } = useConfiguration();
  const [errors, setErrors] = useState({});

  if (!config?.configuration?.attack_feasibility_thresholds) {
    return <div className={styles.noData}>No threshold configuration available</div>;
  }

  const thresholds = config.configuration.attack_feasibility_thresholds;

  /**
   * Single-write path used by every input (the two number boxes and
   * the slider component). Takes the next {min, max} for a level,
   * runs validation, and commits only when valid.
   */
  const applyRange = (level, next) => {
    const updatedLevels = {
      ...thresholds.levels,
      [level]: { ...thresholds.levels[level], ...next },
    };
    const validationErrors = validateThresholds(updatedLevels);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      updateConfiguration({
        ...config,
        configuration: {
          ...config.configuration,
          attack_feasibility_thresholds: { ...thresholds, levels: updatedLevels },
        },
      });
    }
  };

  const handleMinChange = (level, newMin) => {
    const value = parseInt(newMin, 10);
    if (Number.isNaN(value)) return;
    applyRange(level, { min: value });
  };

  const handleMaxChange = (level, newMax) => {
    const value = parseInt(newMax, 10);
    if (Number.isNaN(value)) return;
    applyRange(level, { max: value });
  };

  const validateThresholds = (levels) => {
    const errors = {};

    THRESHOLD_LEVELS.forEach((level) => {
      const threshold = levels[level];
      if (!threshold) return;

      if (threshold.min > threshold.max) {
        errors[level] = 'Minimum must be less than or equal to maximum';
      }
      if (threshold.min < GLOBAL_MIN || threshold.max > GLOBAL_MAX) {
        errors[level] = `Values must be between ${GLOBAL_MIN} and ${GLOBAL_MAX}`;
      }
    });

    // Sort by min so we can scan once for overlaps + gaps.
    const ranges = THRESHOLD_LEVELS
      .map((level) => ({
        level,
        min: levels[level].min,
        max: levels[level].max,
      }))
      .sort((a, b) => a.min - b.min);

    // Overlap detection — adjacent ranges must not share any score.
    for (let i = 0; i < ranges.length - 1; i++) {
      if (ranges[i].max >= ranges[i + 1].min) {
        errors[ranges[i].level] = 'Ranges cannot overlap';
        errors[ranges[i + 1].level] = 'Ranges cannot overlap';
      }
    }

    // Gap detection — every score in [GLOBAL_MIN, GLOBAL_MAX] must be
    // covered by exactly one range. (Adjacent ranges should be
    // [a, b] then [b+1, c]; anything else leaves a gap.)
    if (ranges.length > 0) {
      if (ranges[0].min > GLOBAL_MIN) {
        errors[ranges[0].level] =
          errors[ranges[0].level] ?? `Scores ${GLOBAL_MIN}–${ranges[0].min - 1} are not covered`;
      }
      if (ranges[ranges.length - 1].max < GLOBAL_MAX) {
        errors[ranges[ranges.length - 1].level] =
          errors[ranges[ranges.length - 1].level] ?? `Scores ${ranges[ranges.length - 1].max + 1}–${GLOBAL_MAX} are not covered`;
      }
      for (let i = 0; i < ranges.length - 1; i++) {
        if (ranges[i + 1].min - ranges[i].max > 1) {
          const msg = `Scores ${ranges[i].max + 1}–${ranges[i + 1].min - 1} are not covered`;
          errors[ranges[i].level] = errors[ranges[i].level] ?? msg;
          errors[ranges[i + 1].level] = errors[ranges[i + 1].level] ?? msg;
        }
      }
    }

    return errors;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Attack Feasibility Thresholds</h2>
      </div>

      <div className={styles.thresholdsContainer}>
        {THRESHOLD_LEVELS.map((level) => {
          const threshold = thresholds.levels[level];
          if (!threshold) return null;

          const color = getFeasibilityColor(level);
          const hasError = errors[level];

          return (
            <div key={level} className={styles.thresholdCard}>
              <div className={styles.cardHeader}>
                <div className={styles.levelInfo}>
                  <div className={styles.levelDot} style={{ backgroundColor: color }}></div>
                  <h3>{level}</h3>
                </div>
                <div className={styles.description}>{threshold.description}</div>
              </div>

              <div className={styles.cardBody}>
                <div className={styles.rangeInputs}>
                  <div className={styles.inputGroup}>
                    <label>Minimum Score</label>
                    <Input
                      type="number"
                      min={0}
                      max={59}
                      value={threshold.min}
                      onChange={(e) => handleMinChange(level, e.target.value)}
                      className={styles.input}
                      aria-invalid={hasError ? true : undefined}
                    />
                  </div>

                  <div className={styles.rangeSeparator}>to</div>

                  <div className={styles.inputGroup}>
                    <label>Maximum Score</label>
                    <Input
                      type="number"
                      min={0}
                      max={59}
                      value={threshold.max}
                      onChange={(e) => handleMaxChange(level, e.target.value)}
                      className={styles.input}
                      aria-invalid={hasError ? true : undefined}
                    />
                  </div>
                </div>

                {hasError && (
                  <div className={styles.errorMessage}>
                    {hasError}
                  </div>
                )}

                <Slider
                  value={[threshold.min, threshold.max]}
                  min={GLOBAL_MIN}
                  max={GLOBAL_MAX}
                  color={color}
                  ariaLabels={[`${level} minimum score`, `${level} maximum score`]}
                  onValueChange={([nextMin, nextMax]) =>
                    applyRange(level, { min: nextMin, max: nextMax })
                  }
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Help text */}
      <InstructionPanel title="How to use">
        <ul>
          <li>Define score ranges for each attack feasibility level (Very Low, Low, Medium, High)</li>
          <li>Attack potential scores range from 0 to 59 based on ISO 21434 standard</li>
          <li>Ranges must not overlap and should cover the entire 0-59 range</li>
          <li><strong>ISO 21434 defaults:</strong> Very Low (0-9), Low (10-19), Medium (20-33), High (34-59)</li>
          <li>Higher scores indicate attacks that are more feasible (easier to execute)</li>
          <li>These thresholds are used to classify attack paths in the TARA analysis</li>
          <li>Changes are saved when you click "Save Changes" in the header</li>
        </ul>
      </InstructionPanel>
    </div>
  );
}
